import type { FC } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector } from "@/src/contexts";
import { MgmSharedResources, MgmSharedResourcesEn } from "@/src/member-get-member/shared";
import WebViewNavigation from "@/src/member-get-member/shared/WebViewNavigation";
import { browserStatics } from "@/src/shared/BrowserStatic";
import PageLayout from "@/src/shared/page-layout";
import {
    MgmEventType,
    TelemetryEntity,
    getCdnImage,
    gotoNativeScreen,
    urlFormat,
} from "@Components/Modules/Helpers";
import { scrollToCbBrandsList } from "@Constants/PortalConstants";
import { CashbackDetails } from "@Routes/CashbackLinks";
import { PhoneVerification } from "@Routes/MemberGetMemberLinks";

import type { ICashbackNumbersItem } from "../../cashback/LandingPage/CashbackNumbers";
import CashbackNumbers from "../../cashback/LandingPage/CashbackNumbers";
import PanelSection from "../../shared/panel-section";
import MgmHowItWorks from "../shared/MgmHowItWorks";
import MgmTransactionsSummaryModal from "../shared/MgmTransactionsSummaryModal";
import Cta from "./Cta";
import MgmDashboard from "./MgmDashboard";
import { MgmMainPageResources } from "./MgmMainPage.Resources";
import { MgmMainPageResourcesEn } from "./MgmMainPage.Resources.En";
import type { IMgmUserDetails } from "./MgmMainPage.d";

import * as css from "./MgmMainPage.module.css";

export type IMgmMainPage = {
    mgmUserDetails?: IMgmUserDetails;
    isMobile: boolean;
};

export const MgmMainPage: FC<IMgmMainPage> = ({ mgmUserDetails, isMobile }) => {
    const navigate = useNavigate();
    const { cta } = languageSelector(MgmMainPageResources, MgmMainPageResourcesEn);
    const { mgmNumbersSummary } = languageSelector(MgmSharedResources, MgmSharedResourcesEn);
    const [openTrxModal, setOpenTrxModal] = useState(false);
    const [offNumbers, setOffNumbers] = useState<ICashbackNumbersItem[]>([]);

    useEffect(() => {
        sendTelemetryEvent({
            eventName: `${TelemetryEntity.Cashback}-${MgmEventType.mainPage}`,
            actionType: "View",
        });
    }, []);

    useEffect(() => {
        setOffNumbers([
            {
                numberInfo: `${mgmUserDetails.mgmPendingBonusSum} zł`,
                description: mgmNumbersSummary.step1,
            },
            {
                numberInfo: `${mgmUserDetails.mgmWithdrawnBonusSum + mgmUserDetails.mgmApprovedBonusSum} zł`,
                description: mgmNumbersSummary.step2,
            },
            {
                numberInfo: `${mgmUserDetails.mgmReceiverCount}`,
                description: mgmNumbersSummary.step3,
                buttonText: mgmNumbersSummary.openSummaryBtnLabel,
                button: {
                    appearance: "transparentMagentaAppearance",
                    onClick: () => setOpenTrxModal(true),
                    telemetryCustom: {
                        eventName: `${TelemetryEntity.Cashback}-${MgmEventType.DashboardSummary}`,
                        actionType: "Click",
                    },
                },
            },
        ]);
    }, [mgmUserDetails]);

    const cashbackPageUrl = urlFormat(CashbackDetails, { [scrollToCbBrandsList]: true }, true);

    return (
        <PageLayout isNewGrid breadcrumbs={isMobile && [{ labelKey: "mgm" }]}>
            <div className={css.mgmMainPageComponent}>
                {browserStatics.isMobileApp && <WebViewNavigation />}

                {mgmUserDetails?.shouldValidatePhoneNumber && (
                    <Cta
                        image={process.env.MGM_PhoneVerification}
                        navigateUrl={urlFormat(PhoneVerification, {
                            ownerId: mgmUserDetails.mgmSenderVoucherUserId,
                        })}
                        title={cta.receiverBonusAmount.replace(
                            "{receiverBonus}",
                            mgmUserDetails.receiverBonusAmount,
                        )}
                        description={cta.description}
                        buttonText={cta.button}
                        trackingUrlCustom={{
                            eventName: `${TelemetryEntity.Cashback}-${MgmEventType.mainPageCtaVerifyPhone}`,
                            actionType: "View",
                        }}
                        clickTrackingUrlCustom={{
                            eventName: `${TelemetryEntity.Cashback}-${MgmEventType.mainPageCtaVerifyPhone}`,
                            actionType: "Click",
                        }}
                    />
                )}

                {mgmUserDetails?.hasMGMReceiverPendingBonusTransaction && (
                    <Cta
                        image={getCdnImage(process.env.MGM_PhoneVerification_Success)}
                        title={cta.missingTrxTitle}
                        description={cta.missingTrxDescription}
                        buttonText={cta.missingTrxButton}
                        onClick={() => {
                            if (browserStatics.isMobileApp) {
                                gotoNativeScreen(cashbackPageUrl);
                                return true;
                            }

                            navigate(cashbackPageUrl);
                            return true;
                        }}
                        trackingUrlCustom={{
                            eventName: `${TelemetryEntity.Cashback}-${MgmEventType.mainPageCtaMakeTrx}`,
                            actionType: "View",
                        }}
                        clickTrackingUrlCustom={{
                            eventName: `${TelemetryEntity.Cashback}-${MgmEventType.mainPageCtaMakeTrx}`,
                            actionType: "Click",
                        }}
                    />
                )}

                <MgmDashboard {...mgmUserDetails} />

                {mgmUserDetails.isSummaryVisible && (
                    <PanelSection title={mgmNumbersSummary.title}>
                        <CashbackNumbers
                            numbersList={offNumbers}
                            telemetryCustom={{
                                eventName: `${TelemetryEntity.Cashback}-${MgmEventType.DashboardSummary}`,
                                actionType: "View",
                            }}
                        />
                    </PanelSection>
                )}

                <MgmHowItWorks />

                {openTrxModal && (
                    <MgmTransactionsSummaryModal onClose={() => setOpenTrxModal(false)} {...mgmUserDetails} />
                )}
            </div>
        </PageLayout>
    );
};
